import { DefaultPrivacyLevel, datadogRum } from "@datadog/browser-rum";
import type { RumEvent } from "@datadog/browser-rum";
import { addReactError, reactPlugin } from "@datadog/browser-rum-react";
import type { ClientOnErrorFunction } from "react-router-dom";

// Basecamp screens show user names, emails and Auth0 identities, and Datadog is not treated as
// covered for PHI, so no page text may reach RUM. These settings stay in code (no Datadog remote
// configuration) so a change to them goes through review.

export const REDACTED = "[redacted]";

export const RumEnv = {
  DEV: "dev",
  STAGING: "staging",
  PROD: "prod",
  LOCAL: "local",
  UNKNOWN: "unknown-env",
} as const;

// Literal values from the SDK's RumEvent types.
const RumEventType = { VIEW: "view", ERROR: "error", RESOURCE: "resource" } as const;

const ENV_BY_HOSTNAME: Record<string, string> = {
  "basecamp.dev.fronterahealth.com": RumEnv.DEV,
  "basecamp.staging.fronterahealth.com": RumEnv.STAGING,
  "basecamp.fronterahealth.com": RumEnv.PROD,
  localhost: RumEnv.LOCAL,
};

export const getRumEnv = (hostname: string) => ENV_BY_HOSTNAME[hostname] ?? RumEnv.UNKNOWN;

// /organizations/:orgId/users/:userId puts the viewed user's Auth0 id in the path.
const USER_ID_SEGMENT = /\/users\/[^/?#]+/g;
// Search boxes send typed text as query values (e.g. `?search=`) and S3 URLs carry signatures, so
// values are redacted; keys stay.
const QUERY_VALUE = /([?&][^=&#]*=)[^&#]+/g;

export const scrubUrl = (url: string) =>
  url.replace(USER_ID_SEGMENT, `/users/${REDACTED}`).replace(QUERY_VALUE, `$1${REDACTED}`);

// The SDK writes a stack as "<type>: <message>" and then one "  at <func> @ <url>" line per frame.
// A message can span lines, so only the trailing run of frame lines is kept, for source maps.
const FRAME_LINE = /^ {2}at .+ @ /;

export const scrubStack = (stack: string, type: string | undefined) => {
  const lines = stack.split("\n");
  let firstFrame = lines.length;
  while (firstFrame > 1 && FRAME_LINE.test(lines[firstFrame - 1])) {
    firstFrame -= 1;
  }
  return [`${type ?? "Error"}: ${REDACTED}`, ...lines.slice(firstFrame)].join("\n");
};

export const scrubRumEvent = (event: RumEvent): boolean => {
  event.view.url = scrubUrl(event.view.url);
  if (event.view.referrer) {
    event.view.referrer = scrubUrl(event.view.referrer);
  }

  switch (event.type) {
    case RumEventType.VIEW:
      // The largest image on screen can be a presigned upload URL.
      if (event.view.performance?.lcp?.resource_url) {
        event.view.performance.lcp.resource_url = scrubUrl(event.view.performance.lcp.resource_url);
      }
      break;
    case RumEventType.ERROR:
      // beforeSend can't edit error.causes, so an error that carries causes is dropped.
      if (event.error.causes?.length) {
        return false;
      }
      event.error.message = REDACTED;
      if (event.error.stack) {
        event.error.stack = scrubStack(event.error.stack, event.error.type);
      }
      break;
    case RumEventType.RESOURCE:
      event.resource.url = scrubUrl(event.resource.url);
      break;
  }
  return true;
};

// Passed to RouterProvider, whose root error boundary catches every route's render errors.
export const reportRouterError: ClientOnErrorFunction = (error, { errorInfo }) => {
  addReactError(error instanceof Error ? error : new Error(String(error)), errorInfo ?? {});
};

export const initRum = () => {
  const restEndpoint = import.meta.env.VITE_FRONTERA_REST_ENDPOINT;

  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: "us3.datadoghq.com",
    service: "frontera-basecamp-ui",
    env: getRumEnv(window.location.hostname),
    // Same value build_deploy.yml passes to the source map upload as --release-version.
    version: import.meta.env.VITE_COMMIT_HASH || "<missing-version>",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK,
    // With the mask level, click names come only from data-dd-action-name, else "Masked Element".
    enablePrivacyForActionName: true,
    // Tracing URLs are prefix matches, so an empty endpoint would add trace headers to every request.
    allowedTracingUrls: restEndpoint ? [restEndpoint] : undefined,
    beforeSend: scrubRumEvent,
    plugins: [reactPlugin({ router: true })],
  });
};

export const setRumUser = (id: string) => {
  datadogRum.setUser({ id });
};
